// @givmo/employer-module — the company a signed-in account already WORKS FOR on Givmo (D-EP-60, 5).
//
// An account that administers no company may still be an EMPLOYEE of one here: linked to it (a company
// SSO sign-in exists only for a linked employee, the backend's `sso_signin_service`: "no row, no
// session"; the apps link one too), and `GET /users/me/company`, a consumer route any signed-in account
// may ask, names it. Jonathan's rule (D-EP-60, 2026-10-15): such a person is never offered to register
// a company. The sign-up doors tell them their company is already on Givmo and how to get in instead —
// the request-access screen's "Request access" where join-or-create can file one, its invite view
// ("Ask a Global Admin of {company} to invite you.") everywhere else.
//
// Every failure reads as "no company": not linked (the route's refusal), no Givmo row yet (a sign-up
// part-way), the network, or no answer within the bound. For everyone else the funnel does exactly
// what it did before.

import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { sdk } from '@givmo/api-client';

/** The company an account is an employee of, as the doors name it. */
export interface EmployerOfRecord {
  readonly name: string;
  readonly logoUrl: string | null;
}

const EMPLOYER_OF_RECORD_KEY = 'employer-module-employer-of-record';

/** How long a door waits on the read before treating it as "no company" (the api-client sets none). */
const READ_TIMEOUT_MS = 8_000;

async function readEmployer(): Promise<EmployerOfRecord | null> {
  const read = sdk.companiesGetMyCompany().then(({ data }) => {
    const name = typeof data?.name === 'string' ? data.name.trim() : '';
    if (name === '') return null;
    return { name, logoUrl: typeof data?.logo_url === 'string' ? data.logo_url : null };
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unanswered = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([read.catch(() => null), unanswered]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** The read, keyed per account: the answer belongs to one signed-in uid. */
function employerOfRecordQuery(uid: string) {
  return {
    queryKey: [EMPLOYER_OF_RECORD_KEY, uid] as const,
    queryFn: readEmployer,
    retry: false,
    staleTime: 60_000,
  };
}

/**
 * The signed-in account's company on Givmo, for a screen. `settled` is true once there is an answer
 * (or nothing to ask: no account), so a screen can hold its decision until then.
 */
export function useEmployerOfRecord(user: User | null): {
  settled: boolean;
  company: EmployerOfRecord | null;
} {
  const query = useQuery({
    ...employerOfRecordQuery(user?.uid ?? ''),
    enabled: user !== null,
  });
  return {
    settled: user === null || query.isSuccess || query.isError,
    company: user === null ? null : (query.data ?? null),
  };
}

/** The same answer for a door that decides outside React (a sign-in's landing). */
export async function readEmployerOfRecord(
  queryClient: QueryClient,
  user: User,
): Promise<EmployerOfRecord | null> {
  try {
    return await queryClient.fetchQuery(employerOfRecordQuery(user.uid));
  } catch {
    return null;
  }
}
